import React from 'react'

import { cn } from '../../../../../../lib/utils'

import s from './ActionMessage.module.css'
import animate from '../../../../../styles/animations.module.css'
import { DefaultMessageProps } from '..'
import { Button, ButtonProps } from '../../../../Button'
import { Icon } from '../../../../Icon'

export type ActionMessageProps = {
  id: string
  text: string
  resourceLabel?: string
  changes: {
    label: string
    redValue?: string
    greenValue?: string
    prefix?: string
    handleEdit?: () => void
  }[]
  triggeredAt: {
    label: string
    value?: string
  }
  triggerButton?: ButtonProps
}

export const ActionMessage = (
  props: ActionMessageProps & DefaultMessageProps,
) => {
  const {
    text,
    createdAt,
    author,
    resourceLabel,
    isNewChatMessage,
    triggeredAt,
  } = props

  const [isHidden, setIsHidden] = React.useState(!!triggeredAt.value)

  return (
    <div className="flex flex-col items-center justify-center">
      {isHidden && (
        <div
          className={cn(
            'flex flex-col items-center justify-center min-w-[300px] w-8/12',
            animate.smallTransitionIn,
            {
              [animate.expandAnimation as string]: isNewChatMessage,
            },
          )}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <div className="ml-1 text-xs">
                {triggeredAt.value ? author.name : triggeredAt.label}
              </div>
            </div>
            <div className="text-xs text-accent-foreground/40">{createdAt}</div>
          </div>

          <div
            className={cn(
              s.speech,
              'relative w-full rounded-xl px-2 py-2 border-2 border-dashed text-sm',
            )}
          >
            {text}{' '}
            {resourceLabel && (
              <span className="bg-accent-foreground/10 font-heavy rounded p-1 py-0.5">
                {resourceLabel}
              </span>
            )}
            <div className="flex flex-col gap-2 justify-center">
              {props.changes.map((change, index) => (
                <div className="text-2xs" key={index}>
                  <span className="font-bold uppercase">{change.label}</span>
                  <div className="text-xs flex gap-2 items-center">
                    {change.redValue && (
                      <span className="bg-red-500/50 rounded p-1 px-2 text-red-800">
                        {change.redValue}
                      </span>
                    )}
                    {change.greenValue && change.redValue && (
                      <span className="text-accent-foreground/60">→</span>
                    )}
                    {change.greenValue && (
                      <button
                        onClick={() => change.handleEdit && change.handleEdit()}
                        className="bg-green-500/50 rounded p-1 px-2 text-green-800"
                      >
                        <div className="flex gap-1 items-center">
                          {change.greenValue}
                          {change.handleEdit && (
                            <Icon
                              className="text-2xs"
                              size={12}
                              name="Pencil"
                            />
                          )}
                        </div>
                      </button>
                    )}
                    {change.prefix && (
                      <span className="text-accent-foreground/60">
                        {change.prefix}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {props.triggerButton && (
              <Button
                {...props.triggerButton}
                variant={'outline'}
                leftIcon="Zap"
                className={cn('w-full mt-2')}
              />
            )}
          </div>
        </div>
      )}

      {!props.triggeredAt.value && (
        <Button
          variant="ghost"
          leftIcon={!isHidden ? 'Zap' : undefined}
          className={cn('mt-2', {
            [s.animateAi as string]: !isHidden,
            'text-accent-foreground/60': isHidden,
          })}
          labelClassName={cn({ 'text-transparent': !isHidden })}
          handleClick={() => setIsHidden(!isHidden)}
          label={!isHidden ? 'Suggested Edits' : 'Hide Edits'}
        />
      )}
    </div>
  )
}
